import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getKriticDetails } from '../services/kriticsApi';
import {
  Container,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button
} from '@mui/material';
import ThumbUp from '@mui/icons-material/ThumbUp';
import ThumbDown from '@mui/icons-material/ThumbDown';
import Loading from './Loading';
import Error from './Error';
import EmptyState from './EmptyState';
import MatchScoreIndicator from './MatchScoreIndicator';
import CommonMovies from './CommonMovies';
import FilterSortControls from './FilterSortControls';
import api from '../services/api';

const KriticDetail = () => {
  const { siteIdentifier } = useParams();
  const [kritic, setKritic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [userChecked, setUserChecked] = useState(false);

  const [filters, setFilters] = useState({
    unrankedOnly: false,
    includeAdult: false,
  });
  const [sortBy, setSortBy] = useState('match_score.desc');
  const [userRatings, setUserRatings] = useState(new Map());

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);

      // Fetch user ratings to support "Unranked Only" filter
      const fetchUserRatings = async () => {
        try {
          const ratings = await api.getUserRatings(parsedUser.id);
          setUserRatings(new Map(ratings.map(r => [r.tmdb_id, r.rating])));
        } catch (err) {
          console.error("Failed to fetch user ratings:", err);
        }
      };
      fetchUserRatings();
    }
    setUserChecked(true);
  }, []);

  useEffect(() => {
    if (!userChecked) {
      return;
    }

    const fetchKriticDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const userId = user ? user.id : null;
        const data = await getKriticDetails(siteIdentifier, userId);
        setKritic(data);
      } catch (err) {
        setError(err.message || 'Failed to fetch kritic details.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchKriticDetails();
  }, [siteIdentifier, user, userChecked]);

  const renderRating = (rating) => {
    if (rating.toLowerCase() === 'fresh') {
      return <ThumbUp sx={{ color: 'success.main' }} />;
    }
    if (rating.toLowerCase() === 'rotten') {
      return <ThumbDown sx={{ color: 'error.main' }} />;
    }
    return `Rating: ${rating}`;
  };

  const filteredAndSortedRatings = React.useMemo(() => {
    if (!kritic || !kritic.all_ratings) return [];

    let processed = [...kritic.all_ratings];

    if (filters.unrankedOnly) {
      processed = processed.filter(movie => !userRatings.has(movie.tmdb_id) || userRatings.get(movie.tmdb_id) === 0);
    }

    processed.sort((a, b) => {
      const [field, order] = sortBy.split('.');
      let valA, valB;

      if (field === 'release_date') {
        valA = a.release_date ? new Date(a.release_date) : new Date(0);
        valB = b.release_date ? new Date(b.release_date) : new Date(0);
      } else if (field === 'title') {
        valA = (a.title || '').toLowerCase();
        valB = (b.title || '').toLowerCase();
      } else if (field === 'match_score' || field === 'popularity') {
        // We use normalized_score as a proxy for relevance/match in this context
        valA = a.normalized_score || 0;
        valB = b.normalized_score || 0;
      } else {
        return 0;
      }

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return processed;
  }, [kritic, filters.unrankedOnly, sortBy, userRatings]);

  const clearAll = () => {
    setFilters({ unrankedOnly: false, includeAdult: false });
    setSortBy('match_score.desc');
  };

  if (loading || !userChecked) {
    return <Loading />;
  }

  if (error) {
    return <Error message={error} />;
  }

  if (!kritic) {
    return <EmptyState message="Kritic not found." />;
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button component={Link} to="/kritics" variant="outlined" sx={{ mb: 3 }}>
        Back to Kritics List
      </Button>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
        {kritic.image_url && (
          <Avatar
            src={kritic.image_url}
            alt={`${kritic.first_name} ${kritic.last_name}`}
            sx={{ width: 100, height: 100, mb: 2 }}
          />
        )}
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {kritic.first_name} {kritic.last_name}
        </Typography>
      </Box>

      {user && kritic.match_score > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 3 }}>
          <MatchScoreIndicator score={kritic.match_score} maxScore={kritic.max_match_score} />
          <Typography variant="caption" color="text.secondary">
            Based on {kritic.movies_in_common} movie(s) rated in common.
          </Typography>
        </Box>
      )}

      <CommonMovies movies={kritic.common_movies} />

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" component="h3" gutterBottom>
          All Movie Ratings
        </Typography>

        <FilterSortControls
          filters={filters}
          setFilters={setFilters}
          sortBy={sortBy}
          setSortBy={setSortBy}
          clearAll={clearAll}
        />

        {filteredAndSortedRatings.length > 0 ? (
          <List>
            {filteredAndSortedRatings.map((movie, index) => (
              <React.Fragment key={index}>
                <ListItem alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      src={movie.poster_path ? `https://image.tmdb.org/t/p/w92${movie.poster_path}` : null}
                      alt={movie.title}
                      sx={{ width: 45, height: 68, mr: 2 }}
                    >
                      {!movie.poster_path && movie.title?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Link to={`/movies/${movie.slug || movie.tmdb_id}`} style={{ textDecoration: 'none', color: 'inherit', fontWeight: 'bold' }}>
                        {movie.title}
                      </Link>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        {renderRating(movie.rating)}
                        {movie.release_date && (
                          <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                            ({new Date(movie.release_date).getFullYear()})
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < filteredAndSortedRatings.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography variant="body1" color="text.secondary">
            {kritic.all_ratings && kritic.all_ratings.length > 0 ? "No movies match your filters." : "No ratings available for this kritic."}
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default KriticDetail;